import { observer } from "mobx-react";
import React, { useContext } from "react";
import styled from "styled-components";
import {
  FormQuestion,
  Option,
} from "../../../../api/questionForm/QuestionFormTypes";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../../../WSUtils/util";
import { CVEQuestionStepContext } from "../CVEQuestionStepContext";
import {
  getQuestionType,
  QuestionType,
  validateQuestion,
} from "../QuestionType";
import { useQuestionFormAnswers } from "../useQuestionFormAnswers";
import { useQuestionFormLog } from "../useQuestionFormLog";
import { CVEChoiceOption } from "./CVEChoiceOption";
import { CVETextQuestion } from "./CVETextQuestion";

export const CVEQuestion = observer(({ question }: CVEQuestionProps) => {
  const type = getQuestionType(question);
  const userInfo = useContext(UserInfoContext);
  const questionContext = useContext(CVEQuestionStepContext);
  const questionFormAPI = userInfo.fullAPI.questionFormAPI;

  const L = useLocStore(LocContext.CV);

  let { qfl } = useQuestionFormLog(question.questionFormId);
  const { answers } = useQuestionFormAnswers(qfl?.id);

  let reqs = question.onlyShowIfChosenOptionIds;
  let mandatoryIf = question.mandatoryIfChosenOptionIds;

  answers?.items.forEach((answer) => {
    reqs = reqs.filter((rid) => rid !== answer.questionOptionId);
    mandatoryIf = mandatoryIf.filter((rid) => rid !== answer.questionOptionId);
  });

  const hidden = reqs.length > 0;

  const conditionallyMandatory =
    question.mandatoryIfChosenOptionIds.length > 0 && mandatoryIf.length == 0;

  const isChild = question.orderPosition.includes(".");

  const validation = validateQuestion(question, answers?.items ?? []);
  const showAsInvalid = !validation.ok && questionContext.hasClickedSubmit;

  const lowerLimit = question.options[0].lowerLimit;
  const upperLimit = question.options[0].upperLimit;

  return (
    <QuestionStyles
      className={concatClassnames([
        "cve-question",
        hidden && "cve-question-hidden",
        isChild && "cve-question-child",
        showAsInvalid && "cve-question-invalid",
      ])}
    >
      {showAsInvalid && (
        <div className="p-message p-message-error cve-question-invalid-highlight"></div>
      )}
      <label
        className={concatClassnames([
          "cve-question-label",
          hidden && "cve-question-label-hidden",
        ])}
      >
        {question.orderPosition + ": "}
        {question.question}
        {(question.mandatory || conditionallyMandatory) && (
          <span className="cve-question-mandatory-notification">*</span>
        )}
        {type === QuestionType.NUMBER && (
          <span className="cve-question-number-only-notification">
            {" "}
            {lowerLimit === upperLimit && L.id("89e73d33612b").loc("(tall)")}
            {lowerLimit !== upperLimit && `${lowerLimit} - ${upperLimit}`}
          </span>
        )}
      </label>
      <div className="cve-question-input-container">
        {type === QuestionType.TEXT && (
          <CVETextQuestion question={question} hidden={hidden} />
        )}
        {type === QuestionType.NUMBER && (
          <CVETextQuestion question={question} hidden={hidden} />
        )}

        {type === QuestionType.CHOICE &&
          question.options.map((o) => (
            <CVEChoiceOption question={question} option={o} key={o.id} />
          ))}
      </div>
    </QuestionStyles>
  );
});

export interface CVEQuestionProps {
  question: FormQuestion;
  option?: Option;
  hidden?: boolean;
}

export const QuestionStyles = styled.div`
  &.cve-question {
    margin-top: 1rem;
    position: relative;

    .cve-question-label {
      position: relative;
    }

    .cve-question-input-container {
      position: relative;
    }

    &.cve-question-hidden {
      .cve-question-label {
        color: var(--gray-700);
        opacity: 0.65;
      }
    }

    &.cve-question-child {
      padding-left: 2rem;
    }

    .cve-question-number-only-notification {
      opacity: 0.5;
    }

    .cve-question-mandatory-notification {
      color: var(--red-500);
      font-weight: bold;
    }

    .cve-question-invalid-highlight {
      position: absolute;
      top: -0.4rem;
      left: -0.6rem;
      right: -0.6rem;
      bottom: -0.4rem;
      z-index: 0;
      margin: 0;
    }
  }
`;
